import { MLSID, isNonEmptyString } from '@aloft/toolkit-shared';

import {
  CompPropertyDataState,
  PropertyDataSliceExtraReducer,
  SubjectPropertyDataState,
} from '../../../types.ts';
import { resetImprovementsValue } from '../../actions/resetImprovementsValue.ts';
import { resetPropertyIdentificationValue } from '../../actions/resetPropertyIdentificationValue.ts';
import { submitEditPropertyDetailsForm } from '../../actions/submitEditPropertyDetailsForm.ts';
import { submitEditSubjectPropertyDetailsForm } from '../../actions/submitEditSubjectPropertyDetailsForm.ts';
import { submitObjectiveCharacteristicsForm } from '../../actions/submitObjectiveCharacteristicsForm.ts';
import { propertyDataAdapter } from '../../propertyDataAdapter.ts';
import { getBumpedSyncState } from '../../utils/getBumpedSyncState.ts';
import { getChangedEntityFromEditableData } from '../utils/getChangedEntityFromEditableData.ts';
import { objectiveCharacteristicsFormToEditableData } from '../utils/objectiveCharacteristicsFormToEditableData.ts';

export const submitEditPropertyFormExtraReducers: PropertyDataSliceExtraReducer = (builder) => {
  builder.addCase(submitEditPropertyDetailsForm, (state, action) => {
    const { id, address, mls, mlsListingID, newAutofilledData } = action.payload;

    const current = state.entities[id];
    if (!current) return;
    const isSubject = id === 'subject';

    if (newAutofilledData) {
      const data = isSubject
        ? {
            subjectData: newAutofilledData,
          }
        : {
            compData: newAutofilledData,
          };
      propertyDataAdapter.updateOne(state, {
        id,
        changes: {
          syncState: getBumpedSyncState(current.syncState),
          ...data,
        },
      });
    } else {
      const propertyData = isSubject
        ? (current as SubjectPropertyDataState).subjectData
        : (current as CompPropertyDataState).compData;

      const { propertyIdentification, transaction } = propertyData.editableData;

      propertyDataAdapter.updateOne(
        state,
        getChangedEntityFromEditableData(current, {
          propertyIdentification: {
            ...propertyIdentification,
            address,
          },
          transaction: {
            ...transaction,
            mls: isNonEmptyString(mls) ? (mls as MLSID) : undefined,
            mlsListingID,
          },
        }),
      );
    }
  });

  builder.addCase(submitEditSubjectPropertyDetailsForm, (state, action) => {
    const subject = state.entities.subject as SubjectPropertyDataState | undefined;
    if (!subject) return;

    const currentPropertyIdentification = subject.subjectData.editableData.propertyIdentification;
    const { arv } = subject.subjectData.editableData;

    const propertyType = action.payload.propertyType ?? 'singleFamilyResidence';

    // Force the ARV to have the same property type as subject AIV
    const updatedArv = arv
      ? {
          ...arv,
          improvements: {
            ...arv.improvements,
            propertyType,
          },
        }
      : undefined;

    propertyDataAdapter.updateOne(
      state,
      getChangedEntityFromEditableData(subject, {
        // @ts-expect-error - `hoaFeeAmount` will be converted to `AloftPriceJSON` by `serializeAloftObjectMiddleware`
        propertyIdentification: {
          ...currentPropertyIdentification,
          ...action.payload,
        },
        improvements: {
          ...subject.subjectData.editableData.improvements,
          propertyType,
        },
        arv: updatedArv,
      }),
    );
  });

  builder.addCase(submitObjectiveCharacteristicsForm, (state, action) => {
    const { id } = action.payload;

    const current = state.entities[id];
    if (!current) return;

    const isSubject = id === 'subject';
    const propertyData = isSubject
      ? (current as SubjectPropertyDataState).subjectData
      : (current as CompPropertyDataState).compData;

    const { improvements, site, transaction } = propertyData.editableData;

    const {
      improvements: newImprovements,
      site: newSite,
      transaction: newTransaction,
    } = objectiveCharacteristicsFormToEditableData(action.payload);

    propertyDataAdapter.updateOne(
      state,
      getChangedEntityFromEditableData(current, {
        improvements: {
          ...improvements,
          ...newImprovements,
        },
        site: {
          ...site,
          ...newSite,
        },
        transaction: {
          ...transaction,
          ...newTransaction,
        },
      }),
    );
  });

  builder.addCase(resetPropertyIdentificationValue, (state, action) => {
    const { id, fieldName } = action.payload;
    const current = state.entities[id];
    if (!current) return;

    const isSubject = id === 'subject';
    const propertyData = isSubject
      ? (current as SubjectPropertyDataState).subjectData
      : (current as CompPropertyDataState).compData;

    const { propertyIdentification } = propertyData.editableData;

    propertyDataAdapter.updateOne(
      state,
      getChangedEntityFromEditableData(current, {
        propertyIdentification: {
          ...propertyIdentification,
          [fieldName]: undefined,
        },
      }),
    );
  });

  builder.addCase(resetImprovementsValue, (state, action) => {
    const { id, fieldName } = action.payload;
    const current = state.entities[id];
    if (!current) return;

    const isSubject = id === 'subject';
    const propertyData = isSubject
      ? (current as SubjectPropertyDataState).subjectData
      : (current as CompPropertyDataState).compData;

    const { improvements } = propertyData.editableData;

    propertyDataAdapter.updateOne(
      state,
      getChangedEntityFromEditableData(current, {
        improvements: {
          ...improvements,
          [fieldName]: undefined,
        },
      }),
    );
  });
};
